import React from 'react';
import { Users, Heart, MessageCircle, Shield, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import References from '../components/References';

const ParentsTeens = () => {
  const conversationStarters = [
    "What do your friends think about drinking?",
    "Have you ever felt pressured to drink?",
    "What would you do if someone offered you alcohol?",
    "Do you know the risks of drinking at your age?",
    "How can I support you in making good choices?",
  ];

  const keyMessages = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Delay is Best',
      content: 'The longer young people delay drinking, the better. The teenage brain is still developing until the early 20s, and alcohol can interfere with this development.',
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: 'Your Influence Matters',
      content: 'Parents and whānau have the biggest influence on young people\'s attitudes toward alcohol. Your words and actions make a difference.',
    },
    {
      icon: <MessageCircle className="h-8 w-8" />,
      title: 'Keep Talking',
      content: 'Have ongoing conversations rather than one big talk. Make it normal to discuss alcohol and other challenging topics.',
    },
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: 'Be Informed',
      content: 'Know the facts about alcohol and young people so you can provide accurate information and guidance.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1520857014576-2c4f4c972b57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/80 to-indigo-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Users className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Parents and Teens</h1>
          <p className="text-xl">Step up and have the talk about drinking</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why This Conversation Matters</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto text-center">
            Talking to your teen about alcohol might feel awkward, but it's one of the most important conversations you can have. Research shows that young people who have regular conversations with their parents about alcohol are less likely to drink at a young age.
          </p>
        </div>

        {/* Key Messages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {keyMessages.map((message, index) => (
            <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <div className="mr-3">{message.icon}</div>
                  {message.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed">{message.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tips for Having the Conversation */}
        <Card className="mb-12 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="text-2xl">Tips for Having the Conversation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">1. Choose the Right Time</h3>
              <p className="text-gray-700">
                Find a relaxed moment when you won't be interrupted. Car rides or walks can be good opportunities for natural conversations.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">2. Listen More Than You Talk</h3>
              <p className="text-gray-700">
                Ask open-ended questions and really listen to their answers. This isn't a lecture - it's a conversation.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">3. Stay Calm and Non-Judgmental</h3>
              <p className="text-gray-700">
                If they've already tried alcohol, don't overreact. Thank them for being honest and use it as a chance to talk about safety.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">4. Share Your Values</h3>
              <p className="text-gray-700">
                Explain why you feel the way you do about underage drinking. Your values matter to them, even if they don't show it.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">5. Discuss Peer Pressure</h3>
              <p className="text-gray-700">
                Help them develop strategies for saying no without losing face with friends. Role-play different scenarios together.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">6. Set Clear Expectations</h3>
              <p className="text-gray-700">
                Be clear about your rules and the consequences. But also explain that their safety is more important than getting in trouble.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Conversation Starters */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Conversation Starters</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-6">Not sure how to begin? Try these questions to get the conversation flowing:</p>
            <div className="space-y-3">
              {conversationStarters.map((starter, index) => (
                <div key={index} className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-lg border-l-4 border-purple-500">
                  <p className="text-gray-800 font-medium">"{starter}"</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="mb-12 bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4 text-center">You're Not Alone</h3>
            <p className="text-lg mb-6 text-center">
              Many parents find these conversations challenging. If you need support or advice on talking to your teen about alcohol, help is available.
            </p>
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-lg text-center">
              <p className="text-xl font-bold mb-2">Alcohol Drug Helpline</p>
              <p className="text-lg">Free call: 0800 787 797 | Free text: 8681</p>
              <p className="mt-4 text-sm">Get confidential advice and support anytime</p>
            </div>
          </CardContent>
        </Card>

        {/* References Section */}
        <References />
      </div>
    </div>
  );
};

export default ParentsTeens;